import { Box, DialogContent, IconButton } from "@mui/material"
import { AcceptedChanged, EditDialogClosed, IsAccepted } from "event-definitions"
import { Bound } from "lib/@components/binding/Bound"
import { Done } from "lib/@components/done"
import { ListItemBox } from "lib/@components/ListItemBox"
import { useDialog } from "lib/@hooks/useDialog"
import { noChange } from "lib/@hooks/useRefresh"
import Iconify from "minimals-template/components/Iconify"
import { useState } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { OnlyWithoutComparisonReadOnly } from "./only-without-comparison-read-only"

export function EditModeSwitch({ children, disabled, id }) {
    const [edit, setEdit] = useState(false)
    const context = useBoundContext()
    const showEdit = useDialog(<EditBox context={context}>{children}</EditBox>, { maxWidth: "lg", fullWidth: true })
    const { target, acceptId, noIndividualEditor, canSave } = useBoundContext()
    const isAccepted = IsAccepted.call({ isAccepted: false, id: id ?? acceptId ?? target.id })
    AcceptedChanged.after.useRefresh(noChange)

    return (
        <Bound editMode={edit && !disabled}>
            <Box position="relative">
                {children}
                {!disabled && canSave && (
                    <ListItemBox
                        sx={{
                            position: "absolute",
                            left: -32,
                            top: 20,
                            transform: "translateY(-50%)",
                            visibility: "visible",
                        }}
                    >
                        <Box flex={1} />
                        {!noIndividualEditor && (
                            <OnlyWithoutComparisonReadOnly>
                                <IconButton
                                    aria-label={edit ? "Stop editing" : "Edit"}
                                    disabled={isAccepted}
                                    onClick={toggle}
                                    sx={{
                                        opacity: isAccepted ? 0 : 1,
                                        color: "primary.main",
                                    }}
                                    size="small"
                                >
                                    {!edit ? (
                                        <Iconify icon="material-symbols:edit" />
                                    ) : (
                                        <Iconify icon="material-symbols:edit-off" />
                                    )}
                                </IconButton>
                            </OnlyWithoutComparisonReadOnly>
                        )}
                    </ListItemBox>
                )}
            </Box>
        </Bound>
    )

    function toggle() {
        if (edit) {
            setEdit(false)
        } else if (context.editInDialog) {
            showEditor().catch(console.error)
        } else {
            setEdit(true)
        }
    }

    async function showEditor() {
        await showEdit()
        EditDialogClosed.raise()
    }
}

function EditBox({ children, context }) {
    return (
        <Bound {...context} editMode={true} popupEditor={true}>
            <Box minHeight="20vh" display="flex" flexDirection="column" justifyContent="center">
                <Box flex={1} />
                <DialogContent>{children}</DialogContent>
                <Box flex={1} />
            </Box>
            <Done />
        </Bound>
    )
}
